// One pool on screen: water, actors, props, and the playback of a resolved
// turn. Playback owns presentation time (hitstop, slow motion, speed-up);
// the outcome is already decided by the sim.
import * as THREE from 'three';
import { ONE } from '../sim/num.js';
import { cellsInDisc, cellAt, CELL } from '../sim/grid.js';
import { exitField, blockFrom } from '../sim/pool.js';
import { TUNE } from '../sim/content.js';
import { BASINS } from '../sim/basins.js';
import { PoolWater, CRAB_TINTS } from './water.js';
import { TargetActor } from './targets.js';
import { SHELLS } from '../sim/shells.js';
const PoolWaterRef = PoolWater;

const TPS = 120;
const WADE = -0.07;   // actors wade: only their feet are under the water line
const CRAB_IDS = ['goro', 'bou', 'chika', 'mio'];

export class PoolView {
  constructor({ stage, sprites, fx, audio, pool, t = k => k, pattern = '', phase = 4 }) {
    Object.assign(this, { stage, sprites, fx, audio, t });
    this.basin = BASINS.find(b => b.id === pool.basin);
    this.centre = [this.basin.centre[0] / 1024, this.basin.centre[1] / 1024];
    this.group = new THREE.Group();
    stage.scene.add(this.group);
    // Each crab's colour is the glass of the shell it wears (shell swaps change the art).
    this.tints = pool.crabs.map((c, i) => (c.shell && SHELLS[c.shell] ? new THREE.Color(SHELLS[c.shell].glass) : CRAB_TINTS[i]));
    this.water = new PoolWater(pool, this.centre, { tints: this.tints, pattern, phase });
    this.group.add(this.water.mesh);
    this.now = 0;
    this.crabs = pool.crabs.map(c => {
      const a = sprites.actor(CRAB_IDS[c.id], { shell: c.shell, pattern });
      a.play('idle'); a.t = Math.random();
      this.group.add(a.group);
      return a;
    });
    this.littles = pool.littles.map(l => {
      if (['star', 'lantern', 'float'].includes(l.kind)) {
        const a = new TargetActor(l.kind, { kagee: PoolWaterRef.kagee, name: l.name });
        this.group.add(a.group);
        return a;
      }
      const a = sprites.actor(l.kind);
      if (l.kind === 'seahorse') a.size = 1.3;   // reads as a seahorse only a bit larger than its body
      a.play('idle'); a.t = Math.random();
      this.group.add(a.group);
      return a;
    });
    this.boss = null;
    if (pool.boss) {
      this.boss = sprites.actor('nushi');
      this.boss.floorY = -0.6;
      this.group.add(this.boss.group);
      this.place(this.boss, pool.boss.x, pool.boss.y, -0.6);
    }
    this.props = [];
    for (const r of pool.rocks) this.addProp('rock', r);
    for (const a of pool.anemones) {
      this.addProp('anemoneBase', a);
      const s = sprites.actor('anemone');
      s.play('idle'); s.t = Math.random();
      this.group.add(s.group);
      this.place(s, a.x, a.y, -0.12);
      (this.anemones ||= []).push(s);
    }
    for (const k of pool.kelps) this.addProp('kelp', k);
    this.addRuleThings(pool);
    this.sync(pool);
    this.playback = null;
  }

  // Driftwood, whirlpool mouths, the rival crab and pearls (tonight's rules).
  addRuleThings(pool) {
    const kagee = PoolWaterRef.kagee;
    this.logs = pool.logs.map((l, n) => {
      const r = l.r / ONE;
      const g = new THREE.Group();
      if (kagee) {
        // A branch of driftwood cut from black paper: a crooked limb and two twigs.
        const black = new THREE.MeshBasicMaterial({ color: 0x030206 });
        const limb = (len, w, x, z, a) => { const m = new THREE.Mesh(new THREE.BoxGeometry(len, 0.06, w), black); m.position.set(x, 0, z); m.rotation.y = a; g.add(m); };
        const k = n % 2 ? -1 : 1;
        limb(r * 2.7, r * 0.55, 0, 0, 0.12 * k);
        limb(r * 1.1, r * 0.22, r * 0.55, r * 0.35 * k, -0.7 * k);
        limb(r * 0.8, r * 0.16, -r * 0.7, -r * 0.3 * k, 0.9 * k);
        g.position.y = 0.05;
        this.group.add(g);
        return g;
      }
      const body = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.55, r * 0.6, r * 2.6, 9).rotateZ(Math.PI / 2),
        kagee ? new THREE.MeshBasicMaterial({ color: 0x050409 }) : new THREE.MeshStandardMaterial({ color: 0x6a4a34, roughness: 0.9, flatShading: true }));
      const ring = new THREE.Mesh(new THREE.TorusGeometry(r * 0.45, r * 0.06, 4, 12).rotateY(Math.PI / 2),
        kagee ? new THREE.MeshBasicMaterial({ color: new THREE.Color(0.6, 0.45, 0.25) }) : new THREE.MeshStandardMaterial({ color: 0xb08a5a }));
      ring.position.x = r * 1.31;
      body.castShadow = true;
      g.add(body, ring);
      g.position.y = 0.05;
      this.group.add(g);
      return g;
    });
    const spiralTex = (() => {
      const cv = document.createElement('canvas'); cv.width = cv.height = 64;
      const ctx = cv.getContext('2d');
      ctx.strokeStyle = kagee ? '#030206' : 'rgba(190,240,255,0.9)'; ctx.lineWidth = kagee ? 5 : 3;
      for (let arm = 0; arm < 3; arm++) {
        ctx.beginPath();
        for (let a = 0; a < 9; a += 0.15) { const rr = 2 + a * 3.2, ang = a + arm * 2.09; const x = 32 + Math.cos(ang) * rr, y = 32 + Math.sin(ang) * rr; a ? ctx.lineTo(x, y) : ctx.moveTo(x, y); }
        ctx.stroke();
      }
      const t = new THREE.CanvasTexture(cv); t.colorSpace = THREE.SRGBColorSpace; return t;
    })();
    this.vortices = pool.vortices.flatMap((w, k) => [w.a, w.b].map(m => {
      const mesh = new THREE.Mesh(new THREE.CircleGeometry(w.r / ONE * 1.35, 24).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial(kagee ? { map: spiralTex, transparent: true, depthWrite: false } : { map: spiralTex, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, color: k ? 0xffc0f0 : 0xa0e8ff }));
      const p = this.world(m.x, m.y); mesh.position.set(p.x, 0.03, p.z); mesh.renderOrder = 3;
      this.group.add(mesh);
      return mesh;
    }));
    this.rival = null;
    if (pool.rival) {
      this.rival = this.sprites.actor('goro');
      this.rival.play('idle');
      // A different crab: blood-red glass or a dark tint.
      // The rival is all black paper; only a red eye and red slits show.
      this.rival.group.traverse(o => { if (o.material?.uniforms?.tint) o.material.uniforms.tint.value = new THREE.Color(0.45, 0.02, 0.02); });
      if (this.rival.uniforms.eye) this.rival.uniforms.eye.value = new THREE.Color(2.2, 0.15, 0.1);
      if (this.rival.uniforms.light) this.rival.uniforms.light.value = new THREE.Color(1.1, 0.35, 0.35);
      this.rival.size = 1.1;
      this.group.add(this.rival.group);
    }
    // Lantern nights: the mouth to the open sea, a window of moonlit water cut
    // in the rim; each lantern shows the way the water will carry it.
    if (pool.exit) {
      const r = pool.exit.r / ONE;
      const m = new THREE.Mesh(new THREE.CircleGeometry(r, 40).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ map: seaMouthTexture(), transparent: true, depthWrite: false }));
      const p = this.world(pool.exit.x, pool.exit.y); m.position.set(p.x, 0.04, p.z); m.renderOrder = 3;
      this.exitMark = m; this.group.add(m);
      this.paths = pool.littles.map(() => {
        const line = new THREE.Line(new THREE.BufferGeometry(), new THREE.LineDashedMaterial({ color: new THREE.Color(1.3, 0.75, 0.3), dashSize: 0.14, gapSize: 0.16, transparent: true, opacity: 0.85 }));
        line.renderOrder = 4; line.frustumCulled = false;
        this.group.add(line);
        return line;
      });
    }
    // Float nights: nets hung on the rim, sagging pockets of knotted twine.
    this.netMeshes = pool.nets.map(n => {
      const r = n.r / ONE;
      const g = new THREE.Mesh(new THREE.CircleGeometry(r, 40).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ map: netTexture(kagee), transparent: true, depthWrite: false }));
      g.renderOrder = 3;
      const p = this.world(n.x, n.y); g.position.set(p.x, 0.05, p.z);
      this.group.add(g);
      return g;
    });
    // Star nights: a faint ring round each star the size of the largest pen
    // that still keeps it (a bigger pen lets the reflection fade).
    if (pool.mode === 'stars') {
      const rr = Math.sqrt((TUNE.starPenMax * (CELL / ONE) ** 2) / Math.PI);
      this.starRings = pool.littles.map(() => {
        const ring = new THREE.Mesh(new THREE.RingGeometry(rr - 0.03, rr, 64).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ color: new THREE.Color(0.9, 0.8, 0.55), transparent: true, opacity: 0.09, depthWrite: false }));
        ring.renderOrder = 3;
        this.group.add(ring);
        return ring;
      });
    }
    // Star nights: the stars scooped so far join into tonight's constellation.
    this.starLine = new THREE.Line(new THREE.BufferGeometry(), new THREE.LineBasicMaterial({ color: new THREE.Color(1.5, 1.2, 0.6), transparent: true, opacity: 0.8 }));
    this.starLine.renderOrder = 4; this.starLine.frustumCulled = false;
    this.starPts = [];
    this.group.add(this.starLine);
    const pearlTex = kagee ? pearlShellTexture() : null;
    this.pearls = pool.pearls.map(q => {
      const m = kagee
        ? new THREE.Mesh(new THREE.CircleGeometry(0.3, 20).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ map: pearlTex, transparent: true, depthWrite: false }))
        : new THREE.Mesh(new THREE.SphereGeometry(0.13, 12, 8), new THREE.MeshBasicMaterial({ color: new THREE.Color(1.5, 1.45, 1.3) }));
      const p = this.world(q.x, q.y); m.position.set(p.x, 0.12, p.z);
      this.group.add(m);
      return m;
    });
  }

  world(x, y) { return new THREE.Vector3(this.centre[0] + x / ONE, 0, this.centre[1] + y / ONE); }
  place(actor, x, y, h = WADE) { const w = this.world(x, y); actor.group.position.set(w.x, h, w.z); }

  addProp(type, o) {
    const kit = this.stage.propKit;
    const names = { rock: ['Prop_Rock_A', 'Prop_Rock_B', 'Prop_Rock_C', 'Prop_Rock_D'], anemoneBase: ['Prop_Anemone_Base'], kelp: ['Prop_Kelp_A', 'Prop_Kelp_B'] }[type];
    const src = kit && names.map(n => kit.getObjectByName(n)).filter(Boolean);
    let obj;
    const r = o.r / ONE;
    if (src && src.length) {
      obj = src[(Math.abs(o.x + o.y) >> 8) % src.length].clone();
      obj.visible = true;
      obj.traverse(m => { m.visible = true; if (m.isMesh) { m.castShadow = m.receiveShadow = true; } });
      obj.position.set(0, 0, 0);
      obj.scale.setScalar(r);
      obj.rotation.y = ((o.x * 7 + o.y * 3) % 628) / 100;
    } else if (type === 'rock') {
      obj = new THREE.Mesh(new THREE.DodecahedronGeometry(r, 0), new THREE.MeshStandardMaterial({ color: 0x3a4766, roughness: 0.8, flatShading: true }));
      obj.scale.y = 0.7; obj.castShadow = obj.receiveShadow = true;
    } else if (type === 'kelp') {
      obj = new THREE.Mesh(new THREE.CircleGeometry(r, 10).rotateX(-Math.PI / 2), new THREE.MeshStandardMaterial({ color: 0x2e4a3a, transparent: true, opacity: 0.55 }));
      obj.position.y = 0.02;
    } else {
      obj = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.8, r, 0.25, 8), new THREE.MeshStandardMaterial({ color: 0x5a3046, roughness: 0.9 }));
    }
    const w = this.world(o.x, o.y);
    const holder = new THREE.Group();
    holder.position.set(w.x, type === 'kelp' ? 0 : -0.3, w.z);
    holder.add(obj);
    this.group.add(holder);
    this.props.push(holder);
  }

  // A chevron bobbing over the crab whose turn it is (-1 hides it).
  mark(id) {
    if (!this.marker) {
      let tex;
      if (PoolWaterRef.kagee) {
        // 影絵: a smooth cut-paper pointer with a black edge.
        const cv = document.createElement('canvas'); cv.width = 88; cv.height = 64;
        const ctx = cv.getContext('2d');
        ctx.beginPath(); ctx.moveTo(8, 8); ctx.quadraticCurveTo(44, 16, 80, 8); ctx.quadraticCurveTo(60, 30, 44, 58); ctx.quadraticCurveTo(28, 30, 8, 8); ctx.closePath();
        ctx.fillStyle = '#ffffff'; ctx.fill(); ctx.lineWidth = 6; ctx.lineJoin = 'round'; ctx.strokeStyle = '#05040a'; ctx.stroke();
        tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace;
      } else {
        const cv = document.createElement('canvas'); cv.width = 11; cv.height = 8;
        const ctx = cv.getContext('2d');
        const rows = ['11111111111', '12222222221', '01222222210', '00122222100', '00012221000', '00001210000', '00000100000'];
        rows.forEach((row, y) => [...row].forEach((v, x) => { if (v !== '0') { ctx.fillStyle = v === '1' ? '#0b1026' : '#ffffff'; ctx.fillRect(x, y, 1, 1); } }));
        tex = new THREE.CanvasTexture(cv); tex.magFilter = tex.minFilter = THREE.NearestFilter;
      }
      this.marker = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true }));
      this.marker.scale.set(0.85, 0.62, 1);
      this.marker.renderOrder = 8;
      this.group.add(this.marker);
    }
    if (!this.markRing) {
      this.markRing = new THREE.Mesh(new THREE.RingGeometry(0.62, 0.72, 48).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, depthWrite: false }));
      this.markRing.renderOrder = 3; this.group.add(this.markRing);
    }
    if (id !== this.markId) this.markT = 0;
    if (id >= 0) this.markRing.material.color.copy(this.tints[id]).lerp(new THREE.Color(1, 1, 1), 0.3);
    this.markId = id;
    this.marker.visible = id >= 0;
    if (id >= 0) this.marker.material.color.copy(this.tints[id]).lerp(new THREE.Color(1, 1, 1), 0.2).multiplyScalar(PoolWaterRef.kagee ? 1.0 : 1.3);
  }

  // "!" over little ones the undertow will reach next turn (null clears).
  setDanger(ids) {
    this.danger = ids;
    if (!this.bangTex) {
      if (PoolWaterRef.kagee) {
        // 影絵: a cut-paper "!" in violet with a black edge.
        const cv = document.createElement('canvas'); cv.width = 40; cv.height = 72;
        const ctx = cv.getContext('2d');
        ctx.fillStyle = '#d9b8ff'; ctx.strokeStyle = '#05030a'; ctx.lineWidth = 5; ctx.lineJoin = 'round';
        ctx.beginPath(); ctx.moveTo(10, 6); ctx.lineTo(30, 6); ctx.lineTo(25, 46); ctx.lineTo(15, 46); ctx.closePath(); ctx.stroke(); ctx.fill();
        ctx.beginPath(); ctx.arc(20, 59, 7, 0, Math.PI * 2); ctx.stroke(); ctx.fill();
        this.bangTex = new THREE.CanvasTexture(cv); this.bangTex.colorSpace = THREE.SRGBColorSpace;
      } else {
        const cv = document.createElement('canvas'); cv.width = 5; cv.height = 9;
        const ctx = cv.getContext('2d');
        ['01110', '01210', '01210', '01210', '01210', '00100', '01110', '01210', '01110'].forEach((row, y) => [...row].forEach((v, x) => { if (v !== '0') { ctx.fillStyle = v === '1' ? '#14081f' : '#d9b8ff'; ctx.fillRect(x, y, 1, 1); } }));
        this.bangTex = new THREE.CanvasTexture(cv); this.bangTex.magFilter = this.bangTex.minFilter = THREE.NearestFilter;
      }
      this.bangs = this.littles.map(() => {
        const m = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.bangTex, depthTest: false, transparent: true }));
        m.scale.set(0.24, 0.43, 1); m.renderOrder = 8; m.visible = false; this.group.add(m); return m;
      });
      // A trembling violet ring on the water under each little one in danger.
      this.dangerRings = this.littles.map(() => {
        const m = new THREE.Mesh(new THREE.RingGeometry(0.42, 0.5, 40).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ color: new THREE.Color(0.75, 0.45, 1.0), transparent: true, depthWrite: false }));
        m.renderOrder = 3; m.visible = false; this.group.add(m); return m;
      });
    }
    this.bangs.forEach((b, i) => { b.visible = !!ids?.has(i); });
    this.dangerRings.forEach((r, i) => { r.visible = !!ids?.has(i); });
  }

  // Snap actors and water to a pool state (start of a turn).
  sync(pool) {
    this.pool = pool;
    pool.crabs.forEach((c, i) => this.place(this.crabs[i], c.x, c.y));
    pool.littles.forEach((l, i) => {
      const a = this.littles[i];
      a.group.visible = l.state === 'lost' || a.leaving;
      if (l.state === 'lost') this.place(a, l.x, l.y);
    });
    this.water.syncFromGrid(pool, this.now);
    pool.logs?.forEach((l, i) => { const p = this.world(l.x, l.y); this.logs[i].position.x = p.x; this.logs[i].position.z = p.z; });
    if (this.rival && pool.rival) this.place(this.rival, pool.rival.x, pool.rival.y);
    this.showWays(pool);
    pool.pearls?.forEach((q, i) => { this.pearls[i].visible = !q.taken; });
  }

  showForecast(cells) { this.water.forecast(cells); }

  // Where each lantern will drift (steepest way down the exit field, around
  // the wakes that block next turn) and where each star's ring sits.
  showWays(pool) {
    this.starRings?.forEach((ring, i) => {
      const l = pool.littles[i];
      ring.visible = l.state === 'lost';
      if (ring.visible) { const w = this.world(l.x, l.y); ring.position.set(w.x, 0.03, w.z); }
    });
    if (!this.paths) return;
    const g = pool.grid, f = exitField(g, pool.exit, blockFrom(pool));
    const cellPos = i => this.world(g.x0 + (i % g.w) * CELL + CELL / 2, g.y0 + Math.floor(i / g.w) * CELL + CELL / 2).setY(0.06);
    this.paths.forEach((line, k) => {
      const l = pool.littles[k];
      let i = l.state === 'lost' ? cellAt(g, l.x, l.y) : -1;
      line.visible = i >= 0 && f[i] > 0;
      if (!line.visible) return;
      const pts = [this.world(l.x, l.y).setY(0.06)];
      for (let n = 0; n < 400 && f[i] > 0; n++) {
        let best = i;
        const cx = i % g.w;
        for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
          const nb = i + dx + dy * g.w;
          if ((dx || dy) && cx + dx >= 0 && cx + dx < g.w && nb >= 0 && nb < f.length && f[nb] >= 0 && f[nb] < f[best]) best = nb;
        }
        if (best === i) break;
        i = best;
        if (n % 3 === 2) pts.push(cellPos(i));
      }
      line.geometry.dispose();
      line.geometry = new THREE.BufferGeometry().setFromPoints(pts);
      line.computeLineDistances();
    });
  }

  // ---------------------------------------------------------------- playback

  play(res, { onEvent, onDone, speed = 1 }) {
    this.paths?.forEach(p => { p.visible = false; });   // drift lines are for aiming; sync() redraws them
    this.setDanger(null);
    // Taken little ones, shoved floats and what the shove saves belong to the undertow phase; everything else plays during motion.
    const events = res.events.filter(e => e.type !== 'undertow' && e.type !== 'taken' && e.type !== 'shove' && !e.late).sort((a, b) => a.tick - b.tick);
    this.playback = {
      res, events, next: 0, t: 0, hitstop: 0, slow: 1, speed, phase: 'move',
      onEvent, onDone, last: res.frames.length - 1, painted: new Uint8Array(res.pool.grid.w * res.pool.grid.h),
      undertow: res.events.find(e => e.type === 'undertow'), ring: 0, ringT: 0, wait: 0,
      moving: res.pool.crabs.map(() => false), prevX: res.pool.crabs.map(c => 0),
      penCount: 0, crossCount: 0,
    };
    const S = res.pool.turn;
    this.playback.stamp = S;
    this.buildRibbons(res, S);
    for (const e of res.events) if (e.type === 'launch') {
      const a = this.crabs[e.crab];
      a.play('spin');
      this.playback.moving[e.crab] = true;
    }
  }

  // Bright wake lines: one strip per launched crab, revealed as it travels.
  // Vertices over cells the sim did not paint (the dark) are dropped to zero.
  buildRibbons(res, S) {
    this.ribbons ||= [];
    // Wakes older than last turn no longer block: remove them; dim last turn's.
    for (const r of [...this.ribbons]) {
      if (r.stamp < S - 1) { this.group.remove(r.mesh); r.mesh.geometry.dispose(); r.mesh.material.dispose(); this.ribbons.splice(this.ribbons.indexOf(r), 1); }
      else r.mesh.material.uniforms.fade.value = 0.35;
    }
    const g = res.pool.grid, f = res.frames;
    for (const e of res.events.filter(x => x.type === 'launch')) {
      const c = e.crab, pts = [];
      for (let t = 0; t < f.length; t += 2) pts.push([f[t][c * 2], f[t][c * 2 + 1]]);
      if (pts.length < 2) continue;
      const pos = [], side = [], along = [], on = [], cellsOf = [];
      const W = (PoolWaterRef.kagee ? 0.08 : 0.13) * (res.pool.rules?.bloom > 1000 ? 1.4 : 1);
      for (let k = 0; k < pts.length; k++) {
        const [x, y] = pts[k], [nx, ny] = pts[Math.min(pts.length - 1, k + 1)], [px, py] = pts[Math.max(0, k - 1)];
        let dx = nx - px, dy = ny - py; const l = Math.hypot(dx, dy) || 1; dx /= l; dy /= l;
        const w = this.world(x, y);
        for (const sgn of [-1, 1]) { pos.push(w.x - dy * W * sgn, 0.025, w.z + dx * W * sgn); side.push(sgn); along.push(k * 2); }
        const cx = Math.floor((x - g.x0) / 1024), cy = Math.floor((y - g.y0) / 1024);
        const lit = g.turnOf[cx + cy * g.w] === S ? 1 : 0;
        cellsOf.push(cx + cy * g.w, cx + cy * g.w);
        on.push(lit, lit);
      }
      const idx = [];
      for (let k = 0; k < pts.length - 1; k++) { const a = k * 2; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      geo.setAttribute('side', new THREE.Float32BufferAttribute(side, 1));
      geo.setAttribute('along', new THREE.Float32BufferAttribute(along, 1));
      geo.setAttribute('on', new THREE.Float32BufferAttribute(on, 1));
      geo.setIndex(idx);
      const mat = new THREE.ShaderMaterial({
        transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
        uniforms: { head: { value: 0 }, color: { value: this.tints[c].clone().multiplyScalar(0.85) }, fade: { value: 1 }, now: { value: 0 }, pattern: { value: c }, flash: { value: 0 } },
        vertexShader: 'attribute float side; attribute float along; attribute float on; varying float vSide; varying float vAlong; varying float vOn; void main(){ vSide=side; vAlong=along; vOn=on; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0); }',
        fragmentShader: `uniform float head; uniform vec3 color; uniform float fade; uniform float now; uniform float pattern; uniform float flash;
          varying float vSide; varying float vAlong; varying float vOn;
          void main() {
            if (vAlong > head) discard;
            float a = abs(vSide);
            // A shape per crab as well as a colour: solid, dashed, double, beaded.
            float core = 1.0 - smoothstep(0.35, 0.9, a);
            if (pattern > 0.5 && pattern < 1.5) core *= step(0.45, fract(vAlong / 14.0));
            if (pattern > 1.5 && pattern < 2.5) core = smoothstep(0.25, 0.45, a) * (1.0 - smoothstep(0.75, 1.0, a));
            if (pattern > 2.5) core *= 1.0 - smoothstep(0.25, 0.55, length(vec2(fract(vAlong / 9.0) - 0.5, a * 0.5)));
            float age = clamp((head - vAlong) / 240.0, 0.0, 1.0);
            float fresh = exp(-(head - vAlong) / 18.0);
            float k = vOn * fade * (0.6 + 0.4 * (1.0 - age));
            vec3 c = color * core * (0.75 + flash * 1.2) + color * core * fresh * 0.3;
            gl_FragColor = vec4(min(c * k, vec3(0.95)), 1.0);
          }`,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.renderOrder = 3; mesh.frustumCulled = false;
      this.group.add(mesh);
      this.ribbons.push({ mesh, stamp: S, crab: c, cells: cellsOf });
    }
  }

  hitstop(seconds) { if (this.playback) this.playback.hitstop = Math.max(this.playback.hitstop, seconds); }

  update(dt, camera, reducedMotion) {
    this.now += dt;
    const pb = this.playback;
    if (pb) this.advance(dt, reducedMotion);
    (this.vortices || []).forEach((v, i) => { v.rotation.y = this.now * (i % 2 ? -1.6 : 1.6); });
    if (this.exitMark) this.exitMark.material.opacity = 0.85 + 0.15 * Math.sin(this.now * 1.6);
    this.paths?.forEach(p => { p.material.dashOffset = -this.now * 0.35; });
    (this.pearls || []).forEach((m, i) => { m.position.y = 0.12 + Math.sin(this.now * 2.5 + i) * 0.04; });
    if (this.rival) {
      if (this.rivalDash) {
        const d = this.rivalDash; d.t += dt;
        const k = Math.min(1, d.t / 0.55), e = k * k * (3 - 2 * k);
        this.rival.group.position.lerpVectors(d.from, d.to, e);
        this.rival.flip = d.to.x < d.from.x;
        if (k >= 1) this.rivalDash = null;
      }
      this.rival.update(dt, camera);
    }
    if (this.markRing) {
      const a = this.crabs[this.markId];
      this.markRing.visible = !!this.marker?.visible && !!a;
      if (this.markRing.visible) {
        this.markT = (this.markT || 0) + dt;
        const k = Math.max(0, 1 - this.markT / 1.2);   // a strong pulse as the turn starts, then a steady ring
        this.markRing.position.set(a.group.position.x, 0.045, a.group.position.z);
        this.markRing.scale.setScalar(1 + 0.25 * k * Math.abs(Math.sin(this.markT * 9)));
        this.markRing.material.opacity = 0.65 + 0.3 * k;
      }
    }
    if (this.marker?.visible) {
      const a = this.crabs[this.markId];
      // Above the sprite on screen: step along the camera's up vector.
      const up = new THREE.Vector3(0, 1, 0).applyQuaternion(camera.quaternion);
      this.marker.position.copy(a.group.position).addScaledVector(up, 1.75 + Math.abs(Math.sin(this.now * 4)) * 0.16);
    }
    for (const r of this.ribbons || []) { const u = r.mesh.material.uniforms.flash; if (u.value > 0) u.value = Math.max(0, u.value - dt * 4); }
    const light = this.stage.spriteLight;
    for (const a of [...this.crabs, ...this.littles, ...(this.anemones || [])]) { if (light) a.uniforms.light.value.copy(light); a.update(dt, camera); }
    if (this.boss) this.boss.update(dt, camera);
    this.water.update(this.now, this.stage.moonLight ?? 0.5);
    // Idle life: little ones bob, the dark makes them tremble.
    if (!pb && this.pool) {
      this.pool.littles.forEach((l, i) => {
        const a = this.littles[i];
        if (l.state !== 'lost') return;
        // Floating things ride the moving surface: height and a little tilt.
        const sw = this.water.surfAt(a.group.position.x, a.group.position.z);
        a.group.position.y = WADE + Math.sin(this.now * 2.2 + i) * 0.03 + sw.h * 0.35;
        a.group.rotation.x = sw.sz * 4; a.group.rotation.z = -sw.sx * 4;
        a.offsetX = this.danger?.has(i) ? Math.sin(this.now * 38 + i) * 0.07 * (Math.sin(this.now * 3 + i) > 0 ? 1 : 0.2) : 0;
        const bang = this.bangs?.[i];
        if (bang?.visible) {
          const up = new THREE.Vector3(0, 1, 0).applyQuaternion(camera.quaternion);
          bang.position.copy(a.group.position).addScaledVector(up, 1.05 + Math.abs(Math.sin(this.now * 5 + i)) * 0.08);
        }
        const ring = this.dangerRings?.[i];
        if (ring?.visible) {
          ring.position.set(a.group.position.x, 0.04, a.group.position.z);
          ring.scale.setScalar(1 + 0.12 * Math.sin(this.now * 6 + i));
          ring.material.opacity = 0.55 + 0.35 * Math.sin(this.now * 6 + i);
        }
      });
    }
  }

  advance(dt, reducedMotion) {
    const pb = this.playback;
    if (pb.phase === 'move') {
      if (pb.hitstop > 0) { pb.hitstop -= dt; return; }
      pb.slow += (1 - pb.slow) * Math.min(1, dt * 5);
      pb.t += dt * TPS * pb.speed * pb.slow;
      const tick = Math.min(pb.last, pb.t);
      // Fire due events.
      // Events on the closing tick sit one past the last frame: fire them on arrival.
      while (pb.next < pb.events.length && (pb.events[pb.next].tick <= tick || tick >= pb.last)) {
        const e = pb.events[pb.next++];
        this.onEvent(e, reducedMotion);
        pb.onEvent?.(e);
        if (pb.hitstop > 0) break;
      }
      this.pose(tick);
      for (const r of this.ribbons || []) if (r.stamp === pb.stamp) r.mesh.material.uniforms.head.value = tick;
      if (tick >= pb.last && pb.next >= pb.events.length) {
        pb.phase = 'settle'; pb.wait = 0.25;
        this.crabs.forEach((a, i) => { if (pb.moving[i]) a.play('land', { then: 'idle' }); pb.moving[i] = false; });
      }
    } else if (pb.phase === 'settle') {
      pb.wait -= dt;
      if (pb.wait <= 0) {
        pb.phase = 'undertow';
        const cells = pb.undertow ? pb.undertow.rings.flat().length : 0;
        if (cells) this.audio.undertow(cells);
        pb.ringT = 0;
      }
    } else if (pb.phase === 'undertow') {
      pb.ringT -= dt;
      const rings = pb.undertow?.rings || [];
      if (pb.ringT <= 0 && pb.ring < rings.length) {
        this.water.darken(rings[pb.ring], this.now);
        pb.ring++; pb.ringT = 0.22;
      }
      if (pb.ring >= rings.length && pb.ringT <= 0) {
        // Floats ride ahead of the ink to the water it has not reached.
        for (const e of pb.res.events.filter(x => x.type === 'shove')) {
          const a = this.littles[e.little];
          a.shove = { from: this.world(...e.from).setY(a.group.position.y), to: this.world(e.x, e.y).setY(a.group.position.y), t: 0 };
          this.water.ripple(a.shove.to.x, a.shove.to.z, this.now + 0.3, 0.8);
          pb.onEvent?.(e);
        }
        // A float the shove drops into a net sinks into it once the glide ends (the glide holds its height).
        for (const e of pb.res.events.filter(x => x.late)) { this.onEvent(e); pb.onEvent?.(e); }
        // Taken little ones go down with the ink.
        let k = 0;
        for (const e of pb.res.events.filter(x => x.type === 'taken')) {
          const a = this.littles[e.little];
          a.play('taken');
          this.audio.taken(k++);
          this.fx.ink(...this.xz(e.x, e.y));
          a.leaving = 'down'; a.leaveT = 0;
          pb.onEvent?.(e);
        }
        pb.phase = 'done'; pb.wait = k ? 1.1 : pb.res.events.some(x => x.type === 'shove') ? 0.7 : 0.35;
        // The territorial crab runs and cuts the wakes it crosses.
        const rv = pb.res.events.find(x => x.type === 'rival');
        if (rv && this.rival) {
          this.rivalDash = { t: 0, from: this.world(rv.x, rv.y).setY(-0.07), to: this.world(rv.tx, rv.ty).setY(-0.07) };
          this.rival.play('walk');
          this.audio.rival?.();
          setTimeout(() => {
            this.water.eraseWake(rv.cut);
            const cut = new Set(rv.cut);
            for (const r of this.ribbons || []) {
              const on = r.mesh.geometry.attributes.on;
              let changed = false;
              r.cells.forEach((c, vi) => { if (cut.has(c) && on.array[vi]) { on.array[vi] = 0; changed = true; } });
              if (changed) on.needsUpdate = true;
            }
            this.rival.play('idle');
          }, 450);
          pb.wait = Math.max(pb.wait, 0.8);
        }
      }
    } else if (pb.phase === 'done') {
      pb.wait -= dt;
      if (pb.wait <= 0) {
        this.playback = null;
        this.sync(pb.res.pool);
        pb.onDone?.();
      }
    }
    // Bodies moving through the water push the surface: bow waves and wakes.
    if (this.water.surf) for (const [a, r, k] of [...this.crabs.map(a => [a, 0.3, 1]), ...this.littles.map(a => [a, 0.2, 0.6])]) {
      const p = a.group.position, last = a.surfLast;
      if (a.group.visible && last) {
        const d = Math.hypot(p.x - last.x, p.z - last.z);
        if (d > 0.002 && d < 1.2) this.water.stir(p.x, p.z, r, Math.min(0.05, d * 0.45 * k));
      }
      a.surfLast = { x: p.x, z: p.z };
    }
    // Floats pushed by the undertow glide over half a second.
    this.littles.forEach(a => {
      if (!a.shove) return;
      const s = a.shove; s.t = Math.min(1, s.t + dt / 0.5);
      a.group.position.lerpVectors(s.from, s.to, 1 - (1 - s.t) ** 3);
      if (s.t >= 1) a.shove = null;
    });
    // Leaving animations (saved rise up, taken sink).
    this.littles.forEach(a => {
      if (!a.leaving) return;
      a.leaveT += dt;
      if (a.leaving === 'sea') { a.group.position.addScaledVector(this.seaDir || new THREE.Vector3(0, 0, -1), dt * 1.4); a.uniforms.opacity.value = Math.max(0, 1 - a.leaveT / 3.5); }
      else if (a.leaving === 'net') { a.group.position.y -= dt * 0.25; a.uniforms.opacity.value = Math.max(0, 1 - a.leaveT / 1.2); }
      else if (a.leaving === 'up') { a.group.position.y += dt * (0.6 + a.leaveT * 1.8); a.uniforms.glow.value.setRGB(0.3, 0.7, 0.8).multiplyScalar(Math.min(1, a.leaveT * 2)); a.uniforms.opacity.value = Math.max(0, 1 - a.leaveT / 1.6); }
      else { a.group.position.y -= dt * 0.35; a.uniforms.dim.value = Math.min(1, a.leaveT); a.uniforms.opacity.value = Math.max(0, 1 - a.leaveT / 1.1); }
      if (a.uniforms.opacity.value <= 0) { a.group.visible = false; a.leaving = null; }
    });
  }

  xz(x, y) { return [this.centre[0] + x / ONE, this.centre[1] + y / ONE]; }

  // Interpolated positions at a fractional tick, plus wake painting.
  pose(tick) {
    const pb = this.playback, f = pb.res.frames;
    const i0 = Math.floor(tick), i1 = Math.min(pb.last, i0 + 1), k = tick - i0;
    const A = f[i0], B = f[i1];
    const nc = this.crabs.length;
    for (let c = 0; c < nc; c++) {
      const x = A[c * 2] + (B[c * 2] - A[c * 2]) * k, y = A[c * 2 + 1] + (B[c * 2 + 1] - A[c * 2 + 1]) * k;
      const a = this.crabs[c];
      const prev = a.group.position.clone();
      this.place(a, x, y);
      const dx = a.group.position.x - prev.x;
      if (Math.abs(dx) > 0.002) a.flip = dx < 0;
      if (pb.moving[c]) {
        const sp = a.group.position.distanceTo(prev);
        a.scale.set(1 + Math.min(0.12, sp * 0.4), 1 - Math.min(0.1, sp * 0.3));
        this.paint(c, x, y);
        if (Math.random() < 0.08 + sp * 0.6) this.fx.plankton(a.group.position.x, a.group.position.z, this.tints[c], 1);
        // Spin speed follows real speed.
        a.uniforms && (a.t += sp * 0.6);
      }
    }
    pb.res.pool.littles.forEach((l, j) => {
      const a = this.littles[j];
      if (a.leaving) return;
      const x = A[(nc + j) * 2] + (B[(nc + j) * 2] - A[(nc + j) * 2]) * k, y = A[(nc + j) * 2 + 1] + (B[(nc + j) * 2 + 1] - A[(nc + j) * 2 + 1]) * k;
      const prev = a.group.position.x;
      this.place(a, x, y);
      if (Math.abs(a.group.position.x - prev) > 0.001) a.flip = a.group.position.x < prev;
    });
    const nl = pb.res.pool.littles.length;
    (this.logs || []).forEach((m, j) => {
      const k2 = (nc + nl + j) * 2;
      if (A[k2] === undefined) return;
      const x = A[k2] + (B[k2] - A[k2]) * k, y = A[k2 + 1] + (B[k2 + 1] - A[k2 + 1]) * k;
      const p = this.world(x, y);
      const dx = p.x - m.position.x, dz = p.z - m.position.z;
      if (dx || dz) { m.rotation.y = Math.atan2(-dz, dx); m.children[0].rotation.x += Math.hypot(dx, dz) * 2; }
      m.position.x = p.x; m.position.z = p.z;
    });
    // Ease the crab squash back.
    for (const a of this.crabs) a.scale.lerp(new THREE.Vector2(1, 1), 0.2);
  }

  // Light the wake cells this crab painted in the sim, as it passes them.
  paint(c, x, y) {
    const pb = this.playback, g = pb.res.pool.grid;
    for (const i of cellsInDisc(g, x, y, TUNE.wakeR)) {
      if (pb.painted[i] || g.turnOf[i] !== pb.stamp || !(g.owner[i] & (1 << c))) continue;
      pb.painted[i] = 1;
      this.water.paintWake(i, 1 << c, this.now);
    }
  }

  onEvent(e, reducedMotion) {
    const pb = this.playback;
    const pan = e.x !== undefined ? Math.max(-0.8, Math.min(0.8, (e.x / ONE) / 8)) : 0;
    const at = e.x !== undefined ? this.world(e.x, e.y) : null;
    const strength = Math.min(1, (e.speed || 0) / 700);
    switch (e.type) {
      case 'launch':
        this.water.ripple(at.x, at.z, this.now, 0.8);
        this.fx.splash(at.x, at.z, 0.4 + strength * 0.5);
        break;
      case 'rim': case 'rock': case 'boss': {
        if (e.crab === undefined) { if (strength > 0.1) this.audio.boing(e.speed * 0.5, pan); break; }
        this.audio.stone(e.speed, pan);
        this.water.ripple(at.x, at.z, this.now, 0.5 + strength);
        this.fx.sparks(at.x, at.z, e.nx / ONE, e.ny / ONE, strength);
        if (strength > 0.25) this.fx.splash(at.x, at.z, strength * 0.6);
        const a = this.crabs[e.crab];
        a.scale.set(0.82, 1.16);
        if (strength > 0.55) { this.hitstop(0.035); if (!reducedMotion) this.stage.addTrauma(0.12 * strength); }
        break;
      }
      case 'clack': {
        this.audio.clack(e.speed, pan);
        this.fx.sparks(at.x, at.z, 0, 0, strength + 0.3, new THREE.Color(1.8, 1.6, 1.2));
        this.water.ripple(at.x, at.z, this.now, 1);
        this.hitstop(0.03 + strength * 0.05);
        if (!reducedMotion) this.stage.addTrauma(0.15 + strength * 0.2);
        for (const id of [e.crab, e.other]) if (id !== undefined) this.crabs[id].scale.set(1.25, 0.78);
        break;
      }
      case 'bump': {
        this.audio.boing(e.speed, pan);
        const l = this.littles[e.little];
        if (l.has('puff')) l.play('puff', { restart: true, then: 'idle' }); else l.play('bump', { restart: true, then: 'idle' });
        this.water.ripple(at.x, at.z, this.now, 0.6);
        break;
      }
      case 'thud':
        this.audio.thud?.(e.speed, pan);
        this.water.ripple(at.x, at.z, this.now, 0.9);
        this.fx.splash(at.x, at.z, strength * 0.7);
        if (strength > 0.4) this.hitstop(0.03);
        break;
      case 'vortex': {
        this.audio.vortex?.(pan);
        const from = this.world(e.x, e.y), to = this.world(e.tx, e.ty);
        this.water.ripple(from.x, from.z, this.now, 1); this.water.ripple(to.x, to.z, this.now + 0.05, 1);
        this.fx.splash(to.x, to.z, 0.6);
        break;
      }
      case 'spine':
        this.audio.stone(e.speed * 0.8, pan);
        this.fx.sparks(at.x, at.z, e.nx / ONE, e.ny / ONE, strength, new THREE.Color(1.4, 0.7, 1.2));
        break;
      case 'horn':
        this.audio.horn?.();
        this.fx.popup(this.t('event.horn'), new THREE.Vector3(at.x, 1.6, at.z), 'pen');
        break;
      case 'pearl': {
        this.audio.pearl?.();
        if (this.pearls?.[e.pearl]) this.pearls[e.pearl].visible = false;
        this.fx.rise(at.x, at.z, 8);
        this.fx.popup(this.t('event.pearl'), new THREE.Vector3(at.x, 1.0, at.z), 'pen');
        break;
      }
      case 'rivalHit':
        this.audio.clack(e.speed, pan);
        this.fx.sparks(at.x, at.z, e.nx / ONE, e.ny / ONE, strength, new THREE.Color(1.6, 0.4, 0.3));
        break;
      case 'hug':
        this.audio.hug();
        this.crabs[e.crab].play('stuck');
        pb.moving[e.crab] = false;
        this.anemones?.[e.anemone]?.play('hug', { restart: true, then: 'idle' });
        this.hitstop(0.06);
        break;
      case 'cross':
        this.audio.crossing(pb.crossCount++, pan);
        this.fx.sparks(at.x, at.z, 0, 0, 0.25, new THREE.Color(0.6, 1.5, 1.7));
        break;
      case 'pen': {
        const size = e.cells.length;
        const pts = e.cells.map(i => this.water.cellWorld(i));
        const n = e.saved.length;
        // The pen closing sends one broad swell out from its middle.
        const mid = pts.reduce((m, p) => [m[0] + p[0] / pts.length, m[1] + p[1] / pts.length], [0, 0]);
        this.water.stir(mid[0], mid[1], Math.min(1.6, 0.4 + Math.sqrt(pts.length) * 0.06), 0.12 + Math.min(0.18, n * 0.04));
        const tier = n === 0 ? 0 : n >= 3 || e.allFour ? 2 : 1;
        const step = Math.max(0.0012, Math.min(0.006, 0.45 / size));
        const gel = e.allFour ? 5 : 1 + (e.closer ?? 0);
        if (tier === 0) {
          // Empty water: a quiet shimmer, no fuss.
          this.water.light(e.cells, this.now, step, e.crabs, gel);
          this.audio.penClose(pb.penCount, size, false, 0);
          this.fx.penBurst(pts, 0);
          break;
        }
        // A pen that shelters someone: a beat of stillness, its walls flash
        // in the colours of the crabs that drew them, then the light floods in.
        const beat = tier === 2 ? 0.15 : 0.08;
        this.hitstop(beat);
        for (const r of this.ribbons || []) if (r.stamp >= pb.stamp - 1 && (e.crabs & (1 << r.crab))) { r.mesh.material.uniforms.flash.value = 1; }
        this.stage.focusDip = tier === 2 ? 0.3 : 0.15;
        this.water.light(e.cells, this.now + beat * 0.6, step, e.crabs, gel);
        this.audio.penClose(pb.penCount++, size, e.allFour, n);
        this.fx.penBurst(pts, tier === 2 ? 1 : 0.4);
        if (!reducedMotion) this.stage.addTrauma(tier === 2 ? 0.28 : 0.12);
        if (tier === 2) pb.slow = 0.35;
        e.saved.forEach((id, k) => this.release(id, k));
        const c = pts.reduce((s, p) => [s[0] + p[0], s[1] + p[1]], [0, 0]).map(v => v / pts.length);
        this.fx.popup(`+${n}`, new THREE.Vector3(c[0], 1.2, c[1]), tier === 2 ? 'four' : 'pen');
        if (e.allFour) this.fx.popup(this.t('event.four'), new THREE.Vector3(c[0], 2.6, c[1] - 1.2), 'four big');
        break;
      }
      case 'saved':
        if (e.how !== 'pen') this.release(e.little, 0, e.how);
        break;
      case 'faded': {
        const a = this.littles[e.little];
        a.uniforms.dim.value = 1; a.leaving = 'net'; a.leaveT = 0;
        this.fx.popup(this.t('event.faded'), a.group.position.clone().setY(1.0), '');
        break;
      }
      case 'picture':
        this.audio.poolClear();
        this.fx.popup(this.t('event.picture'), this.world(0, 0).setY(2.2), 'four big');
        this.hitstop(0.12);
        break;
      case 'calm':
        this.water.light(e.cells, this.now, 0.003, 16);
        this.audio.calm();
        this.boss?.play('calm');
        this.hitstop(0.25);
        pb.slow = 0.25;
        if (!reducedMotion) this.stage.addTrauma(0.35);
        e.saved.forEach((id, k) => this.release(id, k));
        break;
    }
  }

  release(id, k, how = 'pen') {
    const a = this.littles[id];
    if (a.leaving) return;
    a.play('saved');
    const kind = this.pool?.littles[id]?.kind;
    if (kind === 'star') {
      // The star joins tonight's constellation and stays, brighter.
      this.starPts.push(a.group.position.clone().setY(0.12));
      this.starLine.geometry.setFromPoints(this.starPts);
      this.audio.pearl?.();
      this.fx.rise(a.group.position.x, a.group.position.z, 6);
      a.uniforms.glow.value.setRGB(0.3, 0.25, 0.1);
      return;
    }
    a.leaving = kind === 'lantern' ? 'sea' : kind === 'float' ? 'net' : 'up';
    a.leaveT = kind === 'lantern' || kind === 'float' ? 0 : -0.25 - k * 0.08;
    this.audio.saved(k);
    if (kind === 'lantern') {
      this.fx.popup(this.t('event.sent', { name: this.pool.littles[id].name || '' }), a.group.position.clone().setY(1.6), 'pen');
      this.seaDir = this.pool.exit ? this.world(this.pool.exit.x, this.pool.exit.y).sub(this.world(0, 0)).setY(0).normalize() : new THREE.Vector3(0, 0, -1);
    } else if (kind === 'float') {
      this.fx.splash(a.group.position.x, a.group.position.z, 0.6);
      this.fx.popup(this.t('event.netted'), a.group.position.clone().setY(1.2), 'pen');
    } else this.fx.rise(a.group.position.x, a.group.position.z);
  }

  dispose() {
    this.stage.scene.remove(this.group);
    this.water.dispose();
    for (const a of [...this.crabs, ...this.littles, ...(this.anemones || [])]) a.dispose();
    this.boss?.dispose();
  }
}

// The open sea seen through the exit: moonlit ripples inside a black rim.
function seaMouthTexture() {
  const cv = document.createElement('canvas'); cv.width = cv.height = 128;
  const ctx = cv.getContext('2d');
  ctx.beginPath(); ctx.arc(64, 64, 62, 0, Math.PI * 2); ctx.clip();
  const g = ctx.createLinearGradient(0, 0, 0, 128);
  g.addColorStop(0, '#1b2b5c'); g.addColorStop(1, '#0a1230');
  ctx.fillStyle = g; ctx.fillRect(0, 0, 128, 128);
  ctx.strokeStyle = 'rgba(255, 236, 190, 0.8)'; ctx.lineCap = 'round';
  for (let k = 0; k < 9; k++) {
    const y = 20 + k * 11, w = 10 + (k % 3) * 9, x = 64 + Math.sin(k * 2.3) * 22;
    ctx.lineWidth = 2 + (k % 2); ctx.beginPath(); ctx.moveTo(x - w, y); ctx.lineTo(x + w, y); ctx.stroke();
  }
  ctx.lineWidth = 7; ctx.strokeStyle = '#020104'; ctx.beginPath(); ctx.arc(64, 64, 59, 0, Math.PI * 2); ctx.stroke();
  const t = new THREE.CanvasTexture(cv); t.colorSpace = THREE.SRGBColorSpace; return t;
}

// A net pocket seen from above: a rope hoop, knotted mesh sagging to a dark centre.
function netTexture(kagee) {
  const cv = document.createElement('canvas'); cv.width = cv.height = 128;
  const ctx = cv.getContext('2d');
  const rope = kagee ? '#030206' : '#8a7a5a';
  ctx.save(); ctx.beginPath(); ctx.arc(64, 64, 60, 0, Math.PI * 2); ctx.clip();
  const g = ctx.createRadialGradient(64, 64, 4, 64, 64, 60);
  g.addColorStop(0, 'rgba(0,0,0,0.75)'); g.addColorStop(1, 'rgba(0,0,0,0.1)');
  ctx.fillStyle = g; ctx.fillRect(0, 0, 128, 128);
  ctx.strokeStyle = rope; ctx.lineWidth = 1.6;
  // Mesh lines bow toward the centre, so the net reads as a pocket.
  for (let k = -5; k <= 5; k++) for (const s of [1, -1]) {
    ctx.beginPath();
    for (let t = -1; t <= 1.001; t += 0.1) {
      const sag = (1 - t * t) * 0.18;
      const u = k / 5 * (1 - sag), x = 64 + (t + u * s) * 44, y = 64 + (t * s - u) * 44;
      t === -1 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  ctx.restore();
  ctx.lineWidth = 6; ctx.strokeStyle = rope; ctx.beginPath(); ctx.arc(64, 64, 58, 0, Math.PI * 2); ctx.stroke();
  const t = new THREE.CanvasTexture(cv); t.colorSpace = THREE.SRGBColorSpace; return t;
}

// A pearl in an open shell, cut from black paper: the pearl is the only light.
function pearlShellTexture() {
  const cv = document.createElement('canvas'); cv.width = cv.height = 96;
  const ctx = cv.getContext('2d');
  ctx.fillStyle = '#030206';
  ctx.beginPath(); ctx.moveTo(48, 86);
  for (let k = 0; k <= 8; k++) { const a = Math.PI * (1.12 + k * 0.095); ctx.quadraticCurveTo(48 + Math.cos(a - 0.05) * 46, 60 + Math.sin(a - 0.05) * 46, 48 + Math.cos(a) * 40, 60 + Math.sin(a) * 40); }
  ctx.closePath(); ctx.fill();
  const g = ctx.createRadialGradient(46, 50, 1, 48, 52, 15);
  g.addColorStop(0, '#fffbea'); g.addColorStop(0.6, '#efe0b8'); g.addColorStop(1, '#8a7a5a');
  ctx.fillStyle = g; ctx.beginPath(); ctx.arc(48, 52, 13, 0, Math.PI * 2); ctx.fill();
  const t = new THREE.CanvasTexture(cv); t.colorSpace = THREE.SRGBColorSpace; return t;
}
